import { useEffect, useState } from 'react';

import { useMutation, useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useLocalStorage } from 'usehooks-ts';

import { NextPrevProps } from '@/pages/my-twin/update-twin/UpdateTwin';
import {
  apiGetTwinDetails,
  apiUpdateMyTwinDetails,
} from '@/services/twins.api';
import { getErrorMessage, getSuccessMessage } from '../helpers/get-message';
import { useAuth } from './useAuth';
import { invalidateQuery } from './useQuery';

const useTwinDetails = ({ handleNext }: Partial<NextPrevProps> = {}) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const shouldRefetch = location?.key === 'sfd7ia8z';
  const [brainFilesPending, setBrainFilesPending] = useState(false);

  const [hasSeenTwinCompletion, setHasSeenTwinCompletion] = useLocalStorage(
    'hasSeenTwinCompletion',
    false
  );
  const [modalState, setModalState] = useState(false);

  const {
    data: selfTwinData,
    isLoading: selfTwinDataLoading,
    isSuccess,
    isFetching,
    isError,
    error,
    isRefetching,
  } = useQuery({
    queryKey: ['apiGetTwinDetails'],
    queryFn: () => apiGetTwinDetails(),
    onSuccess: (data) => {
      setBrainFilesPending(false);
      const brains = data?.data?.data?.categoryDetails;
      const hasUploadingFile = brains?.some((brain) =>
        brain?.brainFiles?.some((file) => file?.status === 'uploading')
      );
      if (hasUploadingFile) {
        setBrainFilesPending(true);
      }
    },
    onError: (err) => {
      setBrainFilesPending(false);
      toast.error(getErrorMessage(err));
    },
    refetchOnWindowFocus: false,
    enabled: isAuthenticated,
    refetchInterval: brainFilesPending && shouldRefetch ? 5000 : false,
    refetchIntervalInBackground: true,
  });

  const statusCode = selfTwinData?.data?.data?.statusDetails?.code as
    | 100
    | 101
    | 102
    | 103
    | 104
    | 105
    | 106;
  // const statusCode = 100 as any;

  const enableNext = statusCode === 100 || statusCode === 101;

  const {
    mutateAsync: updateTwinDetails,
    isLoading: updateTwinDetailsLoading,
    status: updateStatus,
    isSuccess: isUpdated,
  } = useMutation(apiUpdateMyTwinDetails, {
    onSuccess: (res) => {
      invalidateQuery(['apiGetTwinDetails']);
      invalidateQuery(['userTwinPlan']);
      toast.success(getSuccessMessage(res));
      if (handleNext && enableNext) {
        handleNext();
      }
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    },
  });

  useEffect(() => {
    if (statusCode === 103 && isUpdated && isSuccess) {
      if (!hasSeenTwinCompletion) {
        setModalState(true);
        setHasSeenTwinCompletion(true);
      } else {
        toast.success(
          'Your twin has been sent to our team for verification and approval!'
        );
      }
    }
  }, [
    statusCode,
    isUpdated,
    isSuccess,
    hasSeenTwinCompletion,
    setHasSeenTwinCompletion,
  ]);

  const imageAvatarsData = selfTwinData?.data?.data?.imageAvatars;
  const premiumAvatarData =  selfTwinData?.data?.data?.premiumAvatar ||[];
  const videoAvatarsData =
    selfTwinData?.data?.data?.videoAvatars?.filter(Boolean) || [];

  const isVideoChatEnabled = selfTwinData?.data?.data?.isVideoChatEnabled;
  const isPremiumVideoEnabled = selfTwinData?.data?.data?.isPremiumVideoEnabled;

  return {
    selfTwinData,
    selfTwinDataLoading,
    isSuccess,
    updateTwinDetails,
    updateTwinDetailsLoading,
    isFetching,
    updateStatus,
    isError,
    error,
    modalState,
    setModalState,
    setHasSeenTwinCompletion,
    hasSeenTwinCompletion,
    statusCode,
    imageAvatarsData,
    videoAvatarsData,
    isRefetching,
    premiumAvatarData,
    isVideoChatEnabled,
    isPremiumVideoEnabled
  };
};
export default useTwinDetails;
